import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactPlayer from 'react-player';
import Card from '../components/Card';
import Button from '../components/Button';
import ProgressBar from '../components/ProgressBar';
import ProjectAccessModal from '../components/ProjectAccessModal';
import { getApiUrl } from '../utils/apiConfig';
import { getPseudoColor, getVideoStatusClasses, getVideoStatusLabel } from '../utils/colorUtils';
import { Comment } from '../types';
import { useAuthStore } from '../store/authStore';

interface Video {
  id: string;
  title: string;
  description?: string;
  // YouTube fields (optional for FTP storage)
  youtubeId?: string;
  youtubeUrl?: string;
  // FTP fields (optional for YouTube storage)
  ftpPath?: string;
  ftpUrl?: string;
  fileName?: string;
  fileSize?: number;
  // Storage configuration
  storageType: string;
  thumbnailUrl?: string;
  duration?: number;
  status: string;
  version: number;
  projectId: string;
  createdAt: string;
  uploader?: {
    id: string;
    name: string;
    email: string;
  };
  _count?: {
    comments: number;
  };
}


// Helper function to get the correct video URL based on storage type
const getVideoUrl = (video: Video): string => {
  if (video.storageType === 'YOUTUBE') {
    return video.youtubeUrl || '';
  } else if (video.storageType === 'FTP') {
    // Use streaming endpoint for local videos
    return getApiUrl(`/videos/${video.id}/stream`);
  }
  // Fallback to YouTube for backward compatibility
  return video.youtubeUrl || '';
};

const VideoReview: React.FC = () => {
  const { videoId } = useParams<{ videoId: string }>();
  const navigate = useNavigate();
  const { user, token } = useAuthStore();
  const [video, setVideo] = useState<Video | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [played, setPlayed] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userPseudo, setUserPseudo] = useState<string>('');
  const [showPseudoPrompt, setShowPseudoPrompt] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [projectAccessGranted, setProjectAccessGranted] = useState(false);
  const [showProjectAccessModal, setShowProjectAccessModal] = useState(false);
  const [currentProjectId, setCurrentProjectId] = useState<string>('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState<string | null>(null);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [isAdminSelectionMode, setIsAdminSelectionMode] = useState(false);
  const [selectedComments, setSelectedComments] = useState<Set<string>>(new Set());
  const playerRef = React.useRef<any>(null);
  const commentsScrollRef = React.useRef<HTMLDivElement>(null);

  // Mettre à jour le titre de l'onglet avec le nom de la vidéo
  useEffect(() => {
    if (video?.title) {
      document.title = video.title;
    } else {
      document.title = 'Video Review';
    }

    // Cleanup: remettre le titre par défaut quand on quitte la page
    return () => {
      document.title = 'Espace Client – Nathan Jecko';
    };
  }, [video]);

  const loadVideo = useCallback(async () => {
    try {
      const response = await fetch(getApiUrl(`/videos/${videoId}`));
      if (!response.ok) {
        throw new Error('Vidéo introuvable');
      }
      const videoData = await response.json();
      setVideo(videoData);
      setCurrentProjectId(videoData.projectId);

      // Vérifier l'accès au projet
      const savedPassword = localStorage.getItem(`project_${videoData.projectId}_password`);
      if (savedPassword) {
        // Vérifier si le mot de passe sauvegardé est toujours valide
        try {
          const accessResponse = await fetch(getApiUrl(`/projects/${videoData.projectId}/public?password=${savedPassword}`));
          if (accessResponse.ok) {
            setProjectAccessGranted(true);
          } else {
            // Mot de passe expiré ou invalide, demander à nouveau
            setShowProjectAccessModal(true);
          }
        } catch {
          setShowProjectAccessModal(true);
        }
      } else {
        // Pas de mot de passe sauvegardé, demander l'accès
        setShowProjectAccessModal(true);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [videoId]);

  const loadComments = useCallback(async () => {
    try {
      const response = await fetch(getApiUrl(`/comments/video/${videoId}`));
      if (response.ok) {
        const commentsData = await response.json();
        setComments(commentsData);
      }
    } catch (err) {
      // Les commentaires sont optionnels
    }
  }, [videoId]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getCommentColor = (comment: Comment): string => {
    const identifier = comment.authorPseudo || comment.user?.name || 'anonymous';
    return getPseudoColor(identifier);
  };

  const getClosestCommentId = (): string | null => {
    if (comments.length === 0) return null;

    let closestComment = null;
    let smallestDiff = Infinity;

    for (const comment of comments) {
      if (comment.timestamp !== undefined) {
        const diff = Math.abs(comment.timestamp - currentTime);
        if (diff < smallestDiff) {
          smallestDiff = diff;
          closestComment = comment;
        }
      }
    }

    return closestComment?.id || null;
  };

  useEffect(() => {
    // Charger le pseudo depuis le localStorage (sans afficher la modal automatiquement)
    const savedPseudo = localStorage.getItem('videoReview_pseudo');
    if (savedPseudo) {
      setUserPseudo(savedPseudo);
    }

    loadVideo();
    loadComments();
  }, [videoId, loadVideo, loadComments]);

  // Scroll automatique vers le commentaire surbrillé
  useEffect(() => {
    if (comments.length > 0 && commentsScrollRef.current) {
      const closestCommentId = getClosestCommentId();
      if (closestCommentId) {
        // Petit délai pour s'assurer que le DOM est mis à jour
        setTimeout(() => {
          const highlightedComment = document.getElementById(`comment-${closestCommentId}`);
          if (highlightedComment && commentsScrollRef.current) {
            const container = commentsScrollRef.current;

            // Calculer la position relative dans le conteneur
            const commentTop = highlightedComment.offsetTop;

            // Positionner le commentaire en haut du conteneur avec un petit padding
            const targetScrollTop = commentTop - 20;

            // Animation smooth du scroll
            container.scrollTo({
              top: targetScrollTop,
              behavior: 'smooth'
            });
          }
        }, 100);
      }
    }
  }, [currentTime, comments, getClosestCommentId]);

  const renderComment = (comment: Comment, isReply = false) => {
    const displayName = comment.authorPseudo || comment.user?.name || 'Anonyme';
    const color = getCommentColor(comment);
    const isClosest = comment.id === getClosestCommentId();
    const isAdmin = user?.role === 'ADMIN';
    const canDelete = isAdmin || (userPseudo && comment.authorPseudo === userPseudo);
    const isSelected = selectedComments.has(comment.id);

    return (
      <div
        key={comment.id}
        id={`comment-${comment.id}`}
        className={`${isReply ? 'ml-8' : ''} mb-4 p-4 rounded transition-all duration-200 ${
          isClosest
            ? 'bg-blue-50 border-2 border-blue-200 shadow-md'
            : isSelected && isAdminSelectionMode
            ? 'bg-red-50 border-2 border-red-200 shadow-md'
            : 'bg-gray-50'
        }`}
      >
        <div className="flex justify-between items-start mb-2">
          <div className="flex items-start gap-3 flex-1">
            {isAdmin && isAdminSelectionMode && (
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => toggleCommentSelection(comment.id)}
                className="mt-1"
              />
            )}
            <div className="flex-1">
              <p className="font-semibold text-sm">
                <span
                  className="inline-block w-2 h-2 rounded-full mr-2"
                  style={{ backgroundColor: color }}
                ></span>
                {displayName}
              </p>
              <p className="text-xs text-gray-500">
                {comment.timestamp !== undefined && (
                  <button
                    onClick={() => handleSeekToComment(comment.timestamp!)}
                    className="text-[#E25C48] hover:text-[#D14B34] cursor-pointer"
                  >
                    {formatTime(comment.timestamp)}
                  </button>
                )}
                {comment.timestamp !== undefined && ' • '}
                {new Date(comment.createdAt).toLocaleDateString()}
              </p>
            </div>
          </div>
          {canDelete && !isAdminSelectionMode && (
            <button
              onClick={() => handleDeleteComment(comment.id)}
              className="text-red-400 hover:text-red-600 transition-colors duration-200 p-1"
              title="Supprimer le commentaire"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2M10 11v6M14 11v6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}
        </div>
        <p className="text-sm mb-2">{comment.content}</p>
        {comment.resolved && (
          <span className="inline-block bg-green-100 text-green-800 text-xs px-2 py-1 rounded">
            Resolved
          </span>
        )}

        {comment.replies && comment.replies.length > 0 && (
          <div className="mt-4">
            {comment.replies.map((reply) => renderComment(reply, true))}
          </div>
        )}
      </div>
    );
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    if (!userPseudo.trim()) {
      setShowPseudoPrompt(true);
      return;
    }

    try {
      const response = await fetch(getApiUrl(`/comments/video/${videoId}`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          content: newComment,
          timestamp: currentTime,
          authorPseudo: userPseudo.trim(),
        }),
      });

      if (response.ok) {
        const newCommentData = await response.json();
        setComments([newCommentData, ...comments]);
        setNewComment('');
      } else {
        const errorData = await response.json();
        alert('Erreur lors de l\'ajout du commentaire: ' + (errorData.error || 'Erreur inconnue'));
      }
    } catch (err) {
      alert('Erreur réseau lors de l\'ajout du commentaire');
    }
  };

  const handleDeleteComment = (commentId: string) => {
    const isAdmin = user?.role === 'ADMIN';

    // Pour les utilisateurs non-admin, vérifier qu'ils ont un pseudo
    if (!isAdmin && !userPseudo.trim()) {
      alert('Vous devez avoir un pseudo pour supprimer un commentaire');
      return;
    }

    // Afficher la modal de confirmation
    setCommentToDelete(commentId);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteComment = async () => {
    if (!commentToDelete) return;

    const commentId = commentToDelete;
    const isAdmin = user?.role === 'ADMIN';

    try {
      let response;

      if (isAdmin && token) {
        // Utiliser l'API admin avec token d'authentification
        response = await fetch(getApiUrl(`/comments/${commentId}`), {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
      } else {
        // Utiliser l'API pseudo pour les utilisateurs normaux
        response = await fetch(getApiUrl(`/comments/${commentId}/pseudo`), {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            authorPseudo: userPseudo.trim(),
          }),
        });
      }

      if (response.ok) {
        // Remove the comment from the local state
        setComments(comments.filter(comment => {
          // Remove the comment and all its replies
          if (comment.id === commentId) return false;
          if (comment.replies) {
            comment.replies = comment.replies.filter(reply => reply.id !== commentId);
          }
          return true;
        }));
      } else {
        const errorData = await response.json();
        alert('Erreur lors de la suppression du commentaire: ' + (errorData.error || 'Erreur inconnue'));
      }
    } catch (err) {
      alert('Erreur réseau lors de la suppression du commentaire');
    } finally {
      // Fermer la modal
      setShowDeleteConfirm(false);
      setCommentToDelete(null);
    }
  };

  const cancelDeleteComment = () => {
    setShowDeleteConfirm(false);
    setCommentToDelete(null);
  };

  const toggleAdminSelectionMode = () => {
    setIsAdminSelectionMode(!isAdminSelectionMode);
    setSelectedComments(new Set()); // Reset selection when toggling mode
  };

  const toggleCommentSelection = (commentId: string) => {
    const newSelection = new Set(selectedComments);
    if (newSelection.has(commentId)) {
      newSelection.delete(commentId);
    } else {
      newSelection.add(commentId);
    }
    setSelectedComments(newSelection);
  };

  const selectAllComments = () => {
    const allCommentIds = new Set<string>();
    const addCommentIds = (commentList: Comment[]) => {
      commentList.forEach(comment => {
        allCommentIds.add(comment.id);
        if (comment.replies && comment.replies.length > 0) {
          addCommentIds(comment.replies);
        }
      });
    };
    addCommentIds(comments);
    setSelectedComments(allCommentIds);
  };

  const deselectAllComments = () => {
    setSelectedComments(new Set());
  };

  const handleBulkDeleteComments = () => {
    if (selectedComments.size === 0) return;

    // Afficher la modal de confirmation
    setShowBulkDeleteConfirm(true);
  };

  const confirmBulkDeleteComments = async () => {
    try {
      const deletePromises = Array.from(selectedComments).map(commentId =>
        fetch(getApiUrl(`/comments/${commentId}`), {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        })
      );

      await Promise.all(deletePromises);

      // Remove deleted comments from local state
      const removeCommentIds = (commentList: Comment[], idsToRemove: Set<string>): Comment[] => {
        return commentList
          .filter(comment => !idsToRemove.has(comment.id))
          .map(comment => ({
            ...comment,
            replies: comment.replies ? removeCommentIds(comment.replies, idsToRemove) : []
          }));
      };

      setComments(removeCommentIds(comments, selectedComments));
      setSelectedComments(new Set());
      setIsAdminSelectionMode(false);
    } catch (err) {
      alert('Erreur lors de la suppression des commentaires');
    } finally {
      // Fermer la modal
      setShowBulkDeleteConfirm(false);
    }
  };

  const cancelBulkDeleteComments = () => {
    setShowBulkDeleteConfirm(false);
  };

  const handlePseudoSubmit = (pseudo: string) => {
    const trimmedPseudo = pseudo.trim();
    if (trimmedPseudo) {
      setUserPseudo(trimmedPseudo);
      localStorage.setItem('videoReview_pseudo', trimmedPseudo);
      setShowPseudoPrompt(false);
    }
  };

  const handleProjectAccessGranted = () => {
    setProjectAccessGranted(true);
    setShowProjectAccessModal(false);
  };

  const handleChangePseudo = () => {
    setShowPseudoPrompt(true);
  };

  const handleSeekToComment = (timestamp: number) => {
    if (playerRef.current) {
      playerRef.current.seekTo(timestamp, 'seconds');
      setCurrentTime(timestamp);
      setPlayed(timestamp / duration);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <p>Chargement de la vidéo...</p>
      </div>
    );
  }

  if (error || !video) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Card className="text-center">
          <h2 className="text-xl font-bold mb-4">Erreur</h2>
          <p className="mb-4">{error || 'Vidéo introuvable'}</p>
          <Button onClick={() => navigate(-1)}>Retour</Button>
        </Card>
      </div>
    );
  }

  // Modal pour l'accès au projet
  if (showProjectAccessModal && currentProjectId) {
    return (
      <ProjectAccessModal
        projectId={currentProjectId}
        isOpen={showProjectAccessModal}
        onAccessGranted={handleProjectAccessGranted}
        title="Accès à la vidéo"
        description="Pour voir cette vidéo, vous devez d'abord accéder au projet."
      />
    );
  }

  if (!projectAccessGranted) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <p>Vérification de l'accès en cours...</p>
      </div>
    );
  }

  // Modal pour saisir le pseudo
  if (showPseudoPrompt) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <Card className="w-full max-w-md">
          <h2 className="text-xl font-bold mb-4">Commenter cette vidéo</h2>
          <p className="mb-4 text-gray-600">
            Choisissez un pseudo pour vos commentaires. Il sera mémorisé pour vos prochaines visites.
          </p>
          <PseudoInput onSubmit={handlePseudoSubmit} />
        </Card>
      </div>
    );
  }

  // Modal de confirmation de suppression
  if (showDeleteConfirm) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <Card className="w-full max-w-md">
          <h2 className="text-xl font-bold mb-4">Confirmer la suppression</h2>
          <p className="mb-4 text-gray-600">
            Êtes-vous sûr de vouloir supprimer ce commentaire ? Cette action est irréversible.
          </p>
          <div className="flex gap-3 justify-end">
            <Button
              variant="secondary"
              onClick={cancelDeleteComment}
              className="px-4 py-2"
            >
              Annuler
            </Button>
            <Button
              variant="danger"
              onClick={confirmDeleteComment}
              className="px-4 py-2"
            >
              Supprimer
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Modal de confirmation de suppression en masse
  if (showBulkDeleteConfirm) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <Card className="w-full max-w-md">
          <h2 className="text-xl font-bold mb-4">Confirmer la suppression en masse</h2>
          <p className="mb-4 text-gray-600">
            Êtes-vous sûr de vouloir supprimer {selectedComments.size} commentaire(s) ?
            Cette action est irréversible.
          </p>
          <div className="flex gap-3 justify-end">
            <Button
              variant="secondary"
              onClick={cancelBulkDeleteComments}
              className="px-4 py-2"
            >
              Annuler
            </Button>
            <Button
              variant="danger"
              onClick={confirmBulkDeleteComments}
              className="px-4 py-2"
            >
              Supprimer {selectedComments.size} commentaire(s)
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-100 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="mx-auto px-8 py-4 flex justify-between items-start">
          <div className="flex-1">
            <button
              onClick={() => navigate(`/projects/${currentProjectId}`)}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium mb-2"
            >
              ← Retour
            </button>
            <h1 className="text-3xl font-bold">{video.title}</h1>
            {video.description && (
              <p className="text-gray-600 mt-2">{video.description}</p>
            )}
            <div className="flex items-center gap-4 mt-3 text-sm">
              <div className="flex items-center gap-2">
                <strong className="text-gray-600">Statut:</strong>
                <span
                  className={`px-2 py-1 rounded-full text-white text-xs font-medium ${getVideoStatusClasses(video.status)}`}
                >
                  {getVideoStatusLabel(video.status)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <strong className="text-gray-600">Version:</strong>
                <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-xs font-medium">
                  v{video.version}
                </span>
              </div>
              <span className="text-gray-600"><strong>Créée le:</strong> {new Date(video.createdAt).toLocaleDateString()}</span>
              <span className="text-gray-600"><strong>Commentaires:</strong> {comments.length}</span>
            </div>
          </div>
          {video.storageType === 'FTP' && (
            <div className="ml-4">
              <Button
                variant="primary"
                onClick={() => {
                  const downloadUrl = getApiUrl(`/videos/${video.id}/download`);
                  const link = document.createElement('a');
                  link.href = downloadUrl;
                  link.download = video.fileName || `${video.title}.mp4`;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
                className="px-4 py-2 text-sm font-medium rounded-lg shadow-sm transition-all duration-200 flex items-center gap-2"
              >
                Télécharger
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Video player */}
          <div className="lg:col-span-2">
            <Card className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-lg font-semibold">{video.title}</h3>
                <div className="flex gap-2 items-center">
                  {video.fileSize && (
                    <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
                      {(video.fileSize / 1024 / 1024).toFixed(1)} MB
                    </span>
                  )}
                  <span className={`px-2 py-1 text-xs rounded ${
                    video.storageType === 'YOUTUBE'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-blue-100 text-blue-800'
                  }`}>
                    {video.storageType === 'YOUTUBE' ? 'YouTube' : 'Local'}
                  </span>
                </div>
              </div>
              <div className="aspect-video bg-black rounded">
                <ReactPlayer
                  ref={playerRef}
                  url={getVideoUrl(video)}
                  width="100%"
                  height="100%"
                  controls
                  onProgress={(state) => {
                    setPlayed(state.played);
                    setCurrentTime(state.played * duration);
                  }}
                  onDuration={setDuration}
                  config={{
                    youtube: {
                      playerVars: {
                        modestbranding: 1,
                        rel: 0,
                      },
                    },
                  }}
                />
              </div>
            </Card>

            <ProgressBar
              played={played}
              duration={duration}
              comments={comments}
              onSeek={(fraction) => {
                if (playerRef.current) {
                  playerRef.current.seekTo(fraction, 'fraction');
                  setPlayed(fraction);
                  setCurrentTime(fraction * duration);
                }
              }}
            />

          </div>

          {/* Comments sidebar */}

          <div className="flex flex-col" style={{ maxHeight: 'calc(100vh - 280px)' }}>
            <Card className="h-full flex flex-col">
              <div className="flex justify-between items-center mb-4 flex-shrink-0">
                <h2 className="text-lg font-semibold">Commentaires</h2>
                {user?.role === 'ADMIN' && (
                  <div className="flex gap-2">
                    {!isAdminSelectionMode ? (
                      <Button
                        variant="secondary"
                        onClick={toggleAdminSelectionMode}
                        className="px-3 py-1 text-sm"
                      >
                        Sélection multiple
                      </Button>
                    ) : (
                      <>
                        <Button
                          variant="secondary"
                          onClick={selectAllComments}
                          className="px-3 py-1 text-sm"
                          disabled={comments.length === 0}
                        >
                          Tout sélectionner
                        </Button>
                        <Button
                          variant="secondary"
                          onClick={deselectAllComments}
                          className="px-3 py-1 text-sm"
                          disabled={selectedComments.size === 0}
                        >
                          Tout désélectionner
                        </Button>
                        <Button
                          variant="danger"
                          onClick={handleBulkDeleteComments}
                          className="px-3 py-1 text-sm"
                          disabled={selectedComments.size === 0}
                        >
                          Supprimer ({selectedComments.size})
                        </Button>
                        <Button
                          variant="secondary"
                          onClick={toggleAdminSelectionMode}
                          className="px-3 py-1 text-sm"
                        >
                          Annuler
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Section fixe : pseudo et ajout de commentaire */}
              <div className="flex-shrink-0">
                {userPseudo && (
                  <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg mb-4">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">Commentant en tant que :</span>
                      <span
                        className="px-2 py-1 rounded text-xs text-white font-medium"
                        style={{ backgroundColor: getPseudoColor(userPseudo) }}
                      >
                        {userPseudo}
                      </span>
                    </div>
                    <button
                      onClick={handleChangePseudo}
                      className="text-gray-500 hover:text-gray-700 text-sm"
                      title="Changer de pseudo"
                    >
                      ✏️
                    </button>
                  </div>
                )}

                <div className="bg-white p-4 rounded-lg border border-gray-200 mb-4">
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Ajouter un commentaire..."
                    className="w-full p-2 border border-gray-300 rounded mb-2 text-sm"
                    rows={3}
                  />
                  <div className="flex gap-2 justify-end">
                    <Button onClick={handleAddComment} disabled={!newComment.trim()}>
                      Commenter
                    </Button>
                  </div>
                </div>
              </div>

              {/* Section scrollable : liste des commentaires */}
              <div ref={commentsScrollRef} className="flex-1 overflow-y-auto">
                <div className="space-y-2">
                  {comments.map((comment) => renderComment(comment))}
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

// Composant pour saisir le pseudo
const PseudoInput: React.FC<{ onSubmit: (pseudo: string) => void }> = ({ onSubmit }) => {
  const [pseudo, setPseudo] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (pseudo.trim()) {
      onSubmit(pseudo.trim());
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <input
        type="text"
        value={pseudo}
        onChange={(e) => setPseudo(e.target.value)}
        placeholder="Votre pseudo"
        className="w-full p-3 border border-gray-300 rounded-lg mb-4 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        required
        maxLength={20}
        autoFocus
      />
      <Button type="submit" className="w-full" disabled={!pseudo.trim()}>
        Continuer
      </Button>
    </form>
  );
};

export default VideoReview;